import { Injectable } from '@angular/core';
import { FaceDetection } from '@mediapipe/face_detection';
import { Camera } from '@mediapipe/camera_utils';
import { FaceCrop, FaceDetectionInfo, SnapshotData, SnapshotOptions } from 'src/app/interfaces/model';

@Injectable({
  providedIn: 'root'
})
export class FaceDetectionService {
  private faceDetection: FaceDetection;
  private camera!: Camera;
  private videoElement!: HTMLVideoElement;
  private canvasElement!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private frameCount: number = 0;
  private readonly FRAME_SKIP = 5;

  private currentDetections: any[] = [];
  private currentFrame: HTMLVideoElement | null = null;

  onFaceDetected: () => void = () => { };
  onNoFaceDetected: () => void = () => { };
  onSnapshotTaken?: (snapshotData: SnapshotData) => void;

  constructor() {
    this.faceDetection = new FaceDetection({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`;
      }
    });

    this.faceDetection.setOptions({
      model: 'short',
      minDetectionConfidence: 0.6,
      selfieMode: true
    });

    this.faceDetection.onResults(this.onResults.bind(this));
  }

  async initialize(videoElement: HTMLVideoElement, canvasElement: HTMLCanvasElement) {
    this.videoElement = videoElement;
    this.canvasElement = canvasElement;
    this.canvasCtx = canvasElement.getContext('2d')!;
    this.currentFrame = videoElement;

    this.canvasElement.width = 250;
    this.canvasElement.height = 200;

    this.camera = new Camera(videoElement, {
      onFrame: async () => {
        if (this.frameCount % this.FRAME_SKIP === 0) {
          await this.faceDetection.send({ image: videoElement });
        }
        this.frameCount++;

        if (this.frameCount > 1000) {
          this.frameCount = 0;
        }
      },
      width: 250,
      height: 200
    });

    await this.camera.start();
  }

  private onResults(results: any) {
    this.canvasElement.width = results.image.width || 250;
    this.canvasElement.height = results.image.height || 200;

    this.canvasCtx.save();
    this.canvasCtx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);

    this.currentDetections = results.detections || [];

    if (results.detections && results.detections.length > 0) {
      this.onFaceDetected();

      results.detections.forEach((detection: any, index: number) => {
        try {
          // this.drawDetection(detection);
        } catch (error) {
          console.warn('Error drawing detection:', error);
        }
      });
    } else {
      this.onNoFaceDetected();
    }

    this.canvasCtx.restore();
  }

  async takeSnapshot(options: SnapshotOptions = {}): Promise<SnapshotData> {
    if (!this.videoElement || !this.currentFrame) {
      throw new Error('Video not initialized or no current frame available');
    }

    const {
      includeDetections = true,
      cropFaces = true,
      imageFormat = 'image/jpeg',
      quality = 0.9,
      maxWidth,
      maxHeight
    } = options;

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;

    const videoWidth = this.videoElement.videoWidth || this.videoElement.width || 250;
    const videoHeight = this.videoElement.videoHeight || this.videoElement.height || 200;

    let canvasWidth = videoWidth;
    let canvasHeight = videoHeight;

    if (maxWidth && canvasWidth > maxWidth) {
      const ratio = maxWidth / canvasWidth;
      canvasWidth = maxWidth;
      canvasHeight = canvasHeight * ratio;
    }

    if (maxHeight && canvasHeight > maxHeight) {
      const ratio = maxHeight / canvasHeight;
      canvasHeight = maxHeight;
      canvasWidth = canvasWidth * ratio;
    }

    tempCanvas.width = canvasWidth;
    tempCanvas.height = canvasHeight;

    tempCtx.drawImage(this.videoElement, 0, 0, canvasWidth, canvasHeight);

    const fullFrameDataUrl = tempCanvas.toDataURL(imageFormat, quality);

    const snapshotData: SnapshotData = {
      timestamp: new Date(),
      fullFrame: {
        dataUrl: fullFrameDataUrl,
        width: canvasWidth,
        height: canvasHeight
      },
      detections: [],
      faceCrops: []
    };

    // If a cropped photo is needed for face recognition
    if (includeDetections && this.currentDetections.length > 0) {
      for (let i = 0; i < this.currentDetections.length; i++) {
        const detection = this.currentDetections[i];

        if (detection && detection.boundingBox) {
          const bbox = detection.boundingBox;

          const x = (bbox.xCenter * canvasWidth) - (bbox.width * canvasWidth) / 2;
          const y = (bbox.yCenter * canvasHeight) - (bbox.height * canvasHeight) / 2;
          const width = bbox.width * canvasWidth;
          const height = bbox.height * canvasHeight;

          let confidence = this.extractConfidence(detection);

          const detectionInfo: FaceDetectionInfo = {
            id: i,
            boundingBox: {
              x: Math.max(0, x),
              y: Math.max(0, y),
              width: Math.min(width, canvasWidth - x),
              height: Math.min(height, canvasHeight - y)
            },
            confidence: confidence || 0
          };

          snapshotData.detections.push(detectionInfo);

          // Create face crop
          if (cropFaces) {
            const cropCanvas = document.createElement('canvas');
            const cropCtx = cropCanvas.getContext('2d')!;

            cropCanvas.width = detectionInfo.boundingBox.width;
            cropCanvas.height = detectionInfo.boundingBox.height;

            cropCtx.drawImage(
              this.videoElement,
              detectionInfo.boundingBox.x * (videoWidth / canvasWidth),
              detectionInfo.boundingBox.y * (videoHeight / canvasHeight),
              detectionInfo.boundingBox.width * (videoWidth / canvasWidth),
              detectionInfo.boundingBox.height * (videoHeight / canvasHeight),
              0,
              0,
              detectionInfo.boundingBox.width,
              detectionInfo.boundingBox.height
            );

            const faceCrop: FaceCrop = {
              faceId: i,
              dataUrl: cropCanvas.toDataURL(imageFormat, quality),
              boundingBox: detectionInfo.boundingBox,
              confidence: detectionInfo.confidence
            };

            snapshotData.faceCrops.push(faceCrop);
          }
        }
      }
    }

    if (this.onSnapshotTaken) {
      this.onSnapshotTaken(snapshotData);
    }

    return snapshotData;
  }

  canTakeSnapshot(): boolean {
    return !!(this.videoElement && this.currentFrame);
  }

  private extractConfidence(detection: any): number | null {
    if (detection.V && detection.V.length > 0 && detection.V[0].ga && typeof detection.V[0].ga === 'number') {
      return detection.V[0].ga;
    } else if (detection.V && detection.V.length > 0 && typeof detection.V[0] === 'number') {
      return detection.V[0];
    } else if (detection.score && typeof detection.score === 'number') {
      return detection.score;
    } else if (detection.score && detection.score.length > 0 && typeof detection.score[0] === 'number') {
      return detection.score[0];
    } else if (detection.confidence && typeof detection.confidence === 'number') {
      return detection.confidence;
    }
    return null;
  }

  private drawDetection(detection: any) {
    if (!detection || !detection.boundingBox) {
      return;
    }

    const bbox = detection.boundingBox;
    const canvasWidth = this.canvasElement.width;
    const canvasHeight = this.canvasElement.height;

    const x = bbox.xCenter * canvasWidth - (bbox.width * canvasWidth) / 2;
    const y = bbox.yCenter * canvasHeight - (bbox.height * canvasHeight) / 2;
    const width = bbox.width * canvasWidth;
    const height = bbox.height * canvasHeight;

    this.canvasCtx.strokeStyle = '#00FF00';
    this.canvasCtx.lineWidth = 3;
    this.canvasCtx.fillStyle = 'transparent';
    this.canvasCtx.strokeRect(x, y, width, height);

    const cornerSize = 10;
    this.canvasCtx.strokeStyle = '#FF0000';
    this.canvasCtx.lineWidth = 2;

    this.canvasCtx.strokeRect(x - cornerSize / 2, y - cornerSize / 2, cornerSize, cornerSize);
    this.canvasCtx.strokeRect(x + width - cornerSize / 2, y - cornerSize / 2, cornerSize, cornerSize);
    this.canvasCtx.strokeRect(x - cornerSize / 2, y + height - cornerSize / 2, cornerSize, cornerSize);
    this.canvasCtx.strokeRect(x + width - cornerSize / 2, y + height - cornerSize / 2, cornerSize, cornerSize);

    let confidence = this.extractConfidence(detection);
    if (confidence !== null && !isNaN(confidence)) {
      const confidencePercent = (confidence * 100).toFixed(1);
      this.canvasCtx.fillStyle = '#00FF00';
      this.canvasCtx.font = '14px Arial';
      this.canvasCtx.fillText(`${confidencePercent}%`, x, y - 5);
    }
  }

  stop() {
    // Stop the mediapipe camera pump.
    if (this.camera) {
      try { this.camera.stop(); } catch (_) {}
    }

    // The mediapipe Camera.stop() does not reliably release the underlying
    // webcam MediaStream (the camera light can stay on), so explicitly stop
    // every track and detach the stream from the video element. This makes the
    // camera fully release on Chrome, Safari, Firefox, Edge and the native
    // Capacitor webview so it can be cleanly re-initialised later.
    if (this.videoElement) {
      const stream = this.videoElement.srcObject as MediaStream | null;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach((track) => {
          try { track.stop(); } catch (_) {}
        });
      }
      try {
        this.videoElement.pause();
      } catch (_) {}
      this.videoElement.srcObject = null;
    }

    // Drop references so a subsequent initialize() starts a fresh camera.
    this.camera = undefined as any;
    this.currentDetections = [];
    this.currentFrame = null;
    this.frameCount = 0;
  }

  getFrameSkipRate(): number {
    return this.FRAME_SKIP;
  }

  setFrameSkipRate(skipRate: number) {
    if (skipRate >= 1) {
      (this as any).FRAME_SKIP = skipRate;
    }
  }
}